/**
 * Time — the derivation layer.
 *
 * The database stores ONE canonical instant per act (`startAt`/`endAt` as
 * timestamptz) plus the edition's IANA timezone. Every other representation
 * an act has ever needed — epoch milliseconds, an offset-bearing ISO string,
 * a wall-clock label, the day it belongs to, the festival's date range — is
 * derived here.
 *
 * The pilot instead stores each time twice: epoch ms driving all logic, and
 * `+02:00` ISO strings driving all display and calendar export, kept in sync
 * purely by hand with nothing validating them. This module exists so that
 * cannot happen.
 *
 * Two rules hold throughout:
 *
 *  1. NEVER a fixed offset. `+02:00` is correct for OZORA in July and wrong
 *     in November, and wrong on most of the planet. Always the IANA zone.
 *  2. NEVER read the clock inside a function. `now` is always a parameter, so
 *     every time-dependent behaviour is testable without freezing globals.
 */

/** An IANA time zone, e.g. `Europe/Budapest`. Never a fixed offset. */
export type TimeZone = string;

/** A calendar date in a festival's local zone, `YYYY-MM-DD`. */
export type DayBucket = string;

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partsCache = new Map<TimeZone, Intl.DateTimeFormat>();
const offsetCache = new Map<TimeZone, Intl.DateTimeFormat>();

function partsFormatter(timeZone: TimeZone): Intl.DateTimeFormat {
  let f = partsCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsCache.set(timeZone, f);
  }
  return f;
}

function offsetFormatter(timeZone: TimeZone): Intl.DateTimeFormat {
  let f = offsetCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' });
    offsetCache.set(timeZone, f);
  }
  return f;
}

/** Break an instant into its wall-clock parts in a given zone. */
export function zonedParts(instant: Date, timeZone: TimeZone): ZonedParts {
  const parts = partsFormatter(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const p = parts.find((x) => x.type === type);
    return p ? Number(p.value) : 0;
  };
  // `hour12: false` yields hour 24 for midnight in some ICU versions.
  const hour = get('hour') % 24;
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
  };
}

/**
 * The UTC offset in effect at `instant` in `timeZone`, as `+HH:MM`.
 * Computed per instant, so a festival spanning a DST change is correct on
 * both sides of it.
 */
export function offsetAt(instant: Date, timeZone: TimeZone): string {
  const name = offsetFormatter(timeZone)
    .formatToParts(instant)
    .find((p) => p.type === 'timeZoneName')?.value;
  if (!name || name === 'GMT') return '+00:00';
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name);
  if (!m) return '+00:00';
  const [, sign, hh, mm] = m;
  return `${sign}${String(hh).padStart(2, '0')}:${mm ?? '00'}`;
}

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

/**
 * An ISO 8601 string carrying the festival's own offset, e.g.
 * `2026-07-31T20:15:00+02:00`.
 *
 * This is what calendar export and any wall-clock display should use, so a
 * time never depends on where the *viewer* happens to be.
 */
export function toOffsetISO(instant: Date, timeZone: TimeZone): string {
  const p = zonedParts(instant, timeZone);
  return (
    `${p.year}-${pad(p.month)}-${pad(p.day)}` +
    `T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` +
    offsetAt(instant, timeZone)
  );
}

/** Wall-clock time at the festival, `HH:MM`. */
export function wallClock(instant: Date, timeZone: TimeZone): string {
  const p = zonedParts(instant, timeZone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** The pilot's decorative `"20:15 - 22:00"`, derived rather than stored. */
export function timeLabel(start: Date, end: Date, timeZone: TimeZone): string {
  return `${wallClock(start, timeZone)} - ${wallClock(end, timeZone)}`;
}

/**
 * The day an act belongs to: the calendar date of its START in the festival's
 * zone.
 *
 * THE MIDNIGHT RULE. A set running 00:00–01:30 belongs to the day it starts
 * on, not to "the previous night". Tündérke at 00:00 on 31 July is a **31
 * July** act, and appears under Friday 31st in the schedule. This is
 * unintuitive, it is what the pilot does, and every festival app that gets it
 * wrong shows people the wrong day for every late-night set.
 */
export function dayBucket(instant: Date, timeZone: TimeZone): DayBucket {
  const p = zonedParts(instant, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Midnight at the start of a `YYYY-MM-DD` day in the festival's zone. */
export function dayStart(day: DayBucket, timeZone: TimeZone): Date {
  const [y, m, d] = day.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) {
    throw new Error(`invalid day bucket: ${day}`);
  }
  // Guess from UTC, then correct by the offset actually in effect. One
  // correction suffices for every real zone.
  const guess = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
  const off = offsetAt(guess, timeZone);
  const sign = off.startsWith('-') ? 1 : -1;
  const [oh, om] = off.slice(1).split(':').map(Number);
  const corrected = new Date(guess.getTime() + sign * ((oh ?? 0) * 60 + (om ?? 0)) * 60_000);
  // Re-check: a DST boundary can shift the offset between guess and result.
  const off2 = offsetAt(corrected, timeZone);
  if (off2 === off) return corrected;
  const sign2 = off2.startsWith('-') ? 1 : -1;
  const [oh2, om2] = off2.slice(1).split(':').map(Number);
  return new Date(guess.getTime() + sign2 * ((oh2 ?? 0) * 60 + (om2 ?? 0)) * 60_000);
}

/**
 * Turn a festival WALL-CLOCK time into a canonical instant.
 *
 * An admin types "2026-07-31T20:15" and means 20:15 at the festival. This is
 * the only place that conversion happens, so nobody editing a lineup ever has
 * to think about offsets or daylight saving.
 *
 * Two passes: correct by the offset in effect at the guessed instant, then
 * again in case that correction crossed a DST boundary. A third pass would
 * change nothing for any real zone.
 */
export function wallClockToInstant(local: string, timeZone: TimeZone): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local.trim());
  if (!m) throw new Error(`not a local date-time: "${local}" (expected YYYY-MM-DDTHH:MM)`);

  const [, ys, mos, ds, hs, mis, ss] = m;
  const asUtc = Date.UTC(
    Number(ys), Number(mos) - 1, Number(ds), Number(hs), Number(mis), Number(ss ?? 0),
  );

  let instant = new Date(asUtc);
  for (let pass = 0; pass < 2; pass++) {
    const offset = offsetAt(instant, timeZone);
    const sign = offset.startsWith('-') ? -1 : 1;
    const [oh, om] = offset.slice(1).split(':').map(Number);
    const offsetMs = sign * ((oh ?? 0) * 60 + (om ?? 0)) * 60_000;
    const next = new Date(asUtc - offsetMs);
    if (next.getTime() === instant.getTime()) break;
    instant = next;
  }
  return instant;
}

/** Every day bucket that has at least one act starting in it, sorted. */
export function dayBuckets(
  acts: ReadonlyArray<{ startAt: Date }>,
  timeZone: TimeZone,
): DayBucket[] {
  const set = new Set<DayBucket>();
  for (const a of acts) set.add(dayBucket(a.startAt, timeZone));
  return [...set].sort();
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** `"Fri 31 Jul"` — the pilot's day-pill label, derived not stored. */
export function dayLabel(day: DayBucket, timeZone: TimeZone): string {
  const at = dayStart(day, timeZone);
  const wd = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(at);
  const p = zonedParts(at, timeZone);
  return `${wd} ${pad(p.day)} ${MONTHS[p.month - 1]}`;
}

/** Short weekday name in the festival's zone, e.g. `Fri`. */
export function weekday(instant: Date, timeZone: TimeZone): string {
  const p = zonedParts(instant, timeZone);
  const utcNoon = Date.UTC(p.year, p.month - 1, p.day, 12);
  return WEEKDAYS[new Date(utcNoon).getUTCDay()] ?? '';
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/**
 * `"24 July – 4 August 2026"` — the edition's date range.
 *
 * DERIVED, never typed. The pilot has this exact string hardcoded in three
 * independent places (the data file, the countdown overlay and the print
 * header), which is three chances to update two of them.
 */
export function formatDateRange(start: Date, end: Date, timeZone: TimeZone, opts: { short?: boolean } = {}): string {
  const a = zonedParts(start, timeZone);
  const b = zonedParts(end, timeZone);
  // `short`: "24 Jul – 4 Aug 2026", as the account's designs print it.
  const name = (m: number) => (opts.short ? MONTH_NAMES[m - 1]!.slice(0, 3) : MONTH_NAMES[m - 1]!);
  const monthA = name(a.month);
  const monthB = name(b.month);
  if (a.year === b.year && a.month === b.month && a.day === b.day) {
    return `${a.day} ${monthA} ${a.year}`;
  }
  if (a.year !== b.year) {
    return `${a.day} ${monthA} ${a.year} – ${b.day} ${monthB} ${b.year}`;
  }
  if (a.month !== b.month) {
    return `${a.day} ${monthA} – ${b.day} ${monthB} ${b.year}`;
  }
  return `${a.day}–${b.day} ${monthA} ${a.year}`;
}

/**
 * Which day of the festival an instant falls on, counting from an anchor date
 * the organiser chooses.
 *
 * OZORA anchors "Day 1" on 27 July even though gates open on the 24th: the
 * first days are arrivals, and the pilot labels them "Opening day" rather
 * than showing a negative day number.
 */
export function festivalDayNumber(
  instant: Date,
  anchorDay: DayBucket,
  timeZone: TimeZone,
): number {
  const today = dayBucket(instant, timeZone);
  const a = dayStart(today, timeZone).getTime();
  const b = dayStart(anchorDay, timeZone).getTime();
  return Math.round((a - b) / 86_400_000) + 1;
}

/** Whether `now` falls inside the festival window. */
export function isLive(now: Date, gatesOpenAt: Date, endsAt: Date): boolean {
  const t = now.getTime();
  return t >= gatesOpenAt.getTime() && t <= endsAt.getTime();
}

/** Milliseconds until an instant; never negative. */
export function msUntil(now: Date, target: Date): number {
  return Math.max(0, target.getTime() - now.getTime());
}

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

/** Split a remaining duration into the countdown cells the UI renders. */
export function countdown(now: Date, target: Date): Countdown {
  const totalMs = msUntil(now, target);
  const totalSeconds = Math.floor(totalMs / 1000);
  return {
    days: Math.floor(totalSeconds / 86_400),
    hours: Math.floor((totalSeconds % 86_400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    totalMs,
  };
}
